/**
 * "What is X" glossary registry — feeds /what-is/[slug] pages and their
 * FAQPage + DefinedTerm JSON-LD.
 *
 * These pages are AEO-first (ChatGPT / Google AI Overviews / Perplexity).
 * They lead with a 40–60 word "answer box" paragraph and stack schema for
 * definition, FAQ, and breadcrumbs.
 */

export interface WhatIsFaq {
  q: string;
  a: string;
}

export interface WhatIsSection {
  heading: string;
  body: string;
}

export interface WhatIsEntry {
  slug: string;
  /** SEO title, ~55 chars */
  title: string;
  /** Meta description, ~150 chars */
  description: string;
  /** Human display term — "Telemedicine", "Tele-Cardiology" */
  term: string;
  /** H1 for the page */
  h1: string;
  /** 40–60 word answer-box paragraph. Rendered first, doubles as DefinedTerm.description */
  answerBox: string;
  sections: WhatIsSection[];
  faqs: WhatIsFaq[];
  /** Optional path to a related Beam service page for internal linking */
  relatedServicePath?: string;
  /** Label for the related service CTA */
  relatedServiceLabel?: string;
}

export const WHAT_IS_ENTRIES: WhatIsEntry[] = [
  {
    slug: "telemedicine",
    title: "What Is Telemedicine? A Plain-English Definition | Beam",
    description:
      "Telemedicine is the remote delivery of clinical care using video, phone, or asynchronous messaging. Here's what it covers, how it's billed, and where it fits.",
    term: "Telemedicine",
    h1: "What is telemedicine?",
    answerBox:
      "Telemedicine is the delivery of clinical care at a distance using video, phone, or secure messaging. It lets a licensed clinician evaluate, diagnose, or advise on a patient without being in the same room. Modern telemedicine includes tele-visits, asynchronous eConsults between providers, and specialist curbsides that support in-person care.",
    sections: [
      {
        heading: "What telemedicine actually includes",
        body: "Telemedicine covers three main formats. First, live tele-visits — a patient and clinician connect by video. Second, asynchronous eConsults — a treating provider sends a question and clinical data to a specialist who returns a written opinion, billable under CPT 99451 and 99452. Third, curbside consults — informal, provider-to-provider clinical questions that speed care without a new patient appointment.",
      },
      {
        heading: "Who uses telemedicine",
        body: "Rural and community hospitals use telemedicine to access specialists they can't hire full-time. Clinics and primary care groups use it to get same-day input on complex cases. Solo providers use it to keep patients local instead of losing them to a specialty referral. PACE programs use it to keep frail elders in the community.",
      },
      {
        heading: "How premium telemedicine is different",
        body: "Not all telemedicine is equal. Premium telemedicine means the clinician on the other end is board-certified, NPI-verified, and has meaningful post-training experience — not a triage script and a mid-level. Beam's standard is NPI-verified, board-certified, minimum 3 years post-residency (or post-credential for allied roles) on every service line.",
      },
    ],
    faqs: [
      {
        q: "Is telemedicine the same as telehealth?",
        a: "In practice they're used interchangeably. Telehealth is a slightly broader term that can include non-clinical services (remote patient monitoring, provider education). Telemedicine specifically refers to the delivery of clinical care.",
      },
      {
        q: "Is telemedicine covered by insurance?",
        a: "Most commercial insurers, Medicare, and most Medicaid plans cover telemedicine visits and, increasingly, interprofessional eConsults under CPT 99451 and 99452. Coverage varies by state and payer.",
      },
      {
        q: "What credentials should a telemedicine physician have?",
        a: "At minimum, an active US state medical license and board certification (or eligibility) in their specialty. Premium telemedicine platforms like Beam also require a valid NPI and a minimum of 3 years post-residency experience.",
      },
    ],
    relatedServicePath: "/inpatient-clinical-services",
    relatedServiceLabel: "See Beam's telemedicine services",
  },
  {
    slug: "tele-cardiology",
    title: "What Is Tele-Cardiology? Definition, Uses & Billing | Beam",
    description:
      "Tele-cardiology is remote cardiology consultation — curbsides, eConsults, tele-visits, and cardiac read services. Here's how it works and who uses it.",
    term: "Tele-Cardiology",
    h1: "What is tele-cardiology?",
    answerBox:
      "Tele-cardiology is the delivery of cardiology consultation remotely — including curbside questions from primary care, asynchronous eConsults, live tele-visits with patients, and interpretation of EKGs, echos, holters, and event monitors. It's used by rural hospitals, community EDs, and primary care clinics to access board-certified cardiologists without recruiting one in-house.",
    sections: [
      {
        heading: "What tele-cardiology covers",
        body: "The best tele-cardiology programs are tiered. Curbsides handle quick questions — an abnormal EKG, a chest-pain workup, a pre-op clearance. eConsults are documented specialist opinions billable under CPT 99451/99452. Tele-visits are direct patient encounters when the case warrants it. Cardiac read services cover EKG, echo, and ambulatory monitor interpretation.",
      },
      {
        heading: "Who uses tele-cardiology",
        body: "Rural and critical-access hospitals use it so ED patients don't get transferred for questions a cardiologist could answer in five minutes. Community hospitals use it to supplement a small cardiology bench. Primary care clinics use it for arrhythmia triage, heart failure titration, and pre-op clearance without a six-week wait.",
      },
      {
        heading: "How premium tele-cardiology differs",
        body: "Premium tele-cardiology means the reader is a board-certified cardiologist — NPI-verified, with meaningful post-fellowship experience — and the workflow supports both the reading and the treating clinician. Beam requires NPI, board certification, and a minimum of 3 years post-residency on every tele-cardiology case.",
      },
    ],
    faqs: [
      {
        q: "Is tele-cardiology billable?",
        a: "Yes. Tele-visits are billed as standard outpatient E/M codes. Provider-to-provider eConsults are billable under CPT 99451 (specialist) and 99452 (treating provider) when documentation criteria are met. Read services are billed under standard interpretation codes.",
      },
      {
        q: "Can tele-cardiology replace an in-house cardiologist?",
        a: "For most community and critical-access hospitals, yes — for the middle-band cases. Tele-cardiology handles curbsides, eConsults, tele-visits, and reads. Cath lab and invasive procedures still require in-person or transfer.",
      },
      {
        q: "Who qualifies as a tele-cardiologist?",
        a: "A board-certified cardiologist with an active US state medical license. Premium platforms require NPI verification and a minimum of 3 years post-fellowship experience.",
      },
    ],
    relatedServicePath: "/tele-cardiology",
    relatedServiceLabel: "See Beam Tele-Cardiology",
  },
  {
    slug: "tele-hospitalist",
    title: "What Is a Tele-Hospitalist? Definition & Coverage Model | Beam",
    description:
      "A tele-hospitalist is a board-certified internist who provides inpatient hospitalist coverage remotely. Here's how the coverage model works.",
    term: "Tele-Hospitalist",
    h1: "What is a tele-hospitalist?",
    answerBox:
      "A tele-hospitalist is a board-certified internal medicine physician who provides inpatient hospitalist coverage remotely — rounding, admissions, discharges, cross-coverage, and rapid-response support — using video and EHR integration. Tele-hospitalist programs are used by community, critical-access, and micro-hospitals that can't sustain a full in-house hospitalist bench.",
    sections: [
      {
        heading: "What a tele-hospitalist does",
        body: "Same clinical work as an in-house hospitalist: admit patients from the ED, round on inpatients, manage the plan of care, coordinate with nursing and case management, discharge patients with a documented plan, and respond to rapid response and code events by video. The only difference is the physician is not physically on-site.",
      },
      {
        heading: "Common tele-hospitalist coverage models",
        body: "Daytime rounding coverage (the hospital has no in-house hospitalist). Nocturnist coverage (in-house team hands off at night). Weekend and holiday coverage. Cross-coverage that lets a small in-house team take real time off. Hybrid models mix and match. Most programs go live in weeks.",
      },
      {
        heading: "What separates premium tele-hospitalists",
        body: "Rotating locums are the cheap version — different faces every week, no institutional memory. Premium tele-hospitalist programs assign dedicated teams so nurses see the same doctors on every shift. Beam requires NPI verification, board certification, and a minimum of 3 years post-residency for every tele-hospitalist.",
      },
    ],
    faqs: [
      {
        q: "Is tele-hospitalist the same as a locum?",
        a: "No. Locums are typically short-term coverage from a rotating pool. Tele-hospitalist programs assign dedicated physicians who learn your facility and stay long-term.",
      },
      {
        q: "Can tele-hospitalists admit patients?",
        a: "Yes. A credentialed tele-hospitalist admits patients from the ED, writes admission orders, and manages the care plan just like an in-house hospitalist.",
      },
      {
        q: "Do we still need any in-house staff?",
        a: "Yes. The bedside team (nurses, RTs, hospitalist-adjacent providers) still handles hands-on care. The tele-hospitalist is the physician of record — the coverage model varies by hospital.",
      },
    ],
    relatedServicePath: "/tele-hospitalist",
    relatedServiceLabel: "See Beam Tele-Hospitalist",
  },
  {
    slug: "tele-case-management",
    title: "What Is Tele-Case Management? Definition & ROI | Beam",
    description:
      "Tele-case management is remote RN case-management support for hospital ED and inpatient teams. Here's what it does and why hospitals use it.",
    term: "Tele-Case Management",
    h1: "What is tele-case management?",
    answerBox:
      "Tele-case management is remote support from a US-based RN case manager who helps hospital ED and inpatient teams determine observation vs. inpatient status, document to MCG or InterQual guidelines in real time, and prevent avoidable denials. It's used by community hospitals that can't staff 24/7 in-house case management.",
    sections: [
      {
        heading: "What a tele-case manager does",
        body: "Real-time review of ED and inpatient admissions against MCG or InterQual criteria. Live support to the admitting physician on whether the case meets inpatient status. Guideline-based documentation entered into the chart to support the level of care actually delivered. Handoff to concurrent utilization review to prevent denials downstream.",
      },
      {
        heading: "Why hospitals use tele-case management",
        body: "Community hospitals rarely have 24/7 in-house case managers. That means observation-vs-inpatient decisions get made at 2 AM without expert input, then get denied on review three months later. Tele-case management puts a case manager in the loop when the decision is actually being made — protecting revenue and cutting denials.",
      },
      {
        heading: "The premium standard for tele-case management",
        body: "The nurse on the other end should be US-based, actively licensed, and experienced with MCG or InterQual. Beam's tele-case managers are RN-licensed with a minimum of 3 years case management experience — not a script-following contact center.",
      },
    ],
    faqs: [
      {
        q: "Is tele-case management the same as utilization review?",
        a: "Related, not identical. Tele-case management supports the status decision at the point of admission. Utilization review is the concurrent (and retrospective) check on whether admitted care meets criteria. Both use MCG or InterQual.",
      },
      {
        q: "What is the ROI on tele-case management?",
        a: "The biggest lever is fewer avoidable observation-vs-inpatient reclassifications on the back end and fewer denials. For a mid-size community hospital, this typically means six- to seven-figure annual revenue protection.",
      },
      {
        q: "Who provides tele-case management?",
        a: "US-based RN case managers licensed in the states they cover. Beam's tele-case managers have a minimum of 3 years of case management experience and are actively verified against OIG / SAM.",
      },
    ],
    relatedServicePath: "/tele-case-management",
    relatedServiceLabel: "See Beam Tele-Case Management",
  },
  {
    slug: "tele-stroke",
    title: "What Is Tele-Stroke? Definition, tPA & Thrombectomy | Beam",
    description:
      "Tele-stroke is real-time video neurology consultation for suspected acute stroke. Here's how it works, who staffs it, and where it fits.",
    term: "Tele-Stroke",
    h1: "What is tele-stroke?",
    answerBox:
      "Tele-stroke is real-time, video-based neurology consultation for patients with suspected acute stroke — most commonly in an emergency department. A board-certified stroke neurologist evaluates the patient by video, calculates NIHSS, reviews imaging, and helps guide tPA/TNK administration and thrombectomy transfer decisions. Time is brain, so response is measured in minutes.",
    sections: [
      {
        heading: "How a tele-stroke consult runs",
        body: "The ED activates tele-stroke on a suspected stroke patient. A tele-stroke neurologist joins by cart-based or bedside video within minutes. The neurologist takes the history, calculates NIHSS, reviews the CT and CTA imaging, and recommends tPA/TNK administration or transfer to a thrombectomy-capable center. Everything is documented in the chart.",
      },
      {
        heading: "Who staffs tele-stroke",
        body: "Board-certified neurologists — many with vascular / stroke fellowship training. Premium tele-stroke programs require NPI verification and a minimum of 3 years post-residency. It should not be a mid-level triage line or phone-only service.",
      },
      {
        heading: "Where tele-stroke fits",
        body: "Rural, critical-access, and community hospital emergency departments without an on-site neurologist. Tele-stroke lets those EDs safely administer tPA, avoid unnecessary transfers, and quickly triage the patients who do need a comprehensive stroke center.",
      },
    ],
    faqs: [
      {
        q: "How fast is a tele-stroke response?",
        a: "Best-in-class tele-stroke programs target a neurologist on video within minutes of activation. Every minute of untreated ischemic stroke costs an estimated 1.9 million neurons.",
      },
      {
        q: "Can a tele-stroke physician order tPA?",
        a: "Yes. A credentialed tele-stroke neurologist can evaluate the patient, calculate NIHSS, and recommend tPA/TNK. The order is co-signed per hospital protocol.",
      },
      {
        q: "Does tele-stroke replace transferring stroke patients?",
        a: "Sometimes. For ischemic strokes eligible for IV thrombolytics only, tele-stroke can enable safe treatment locally. For large vessel occlusions requiring thrombectomy, tele-stroke helps triage which patients truly need transfer to a comprehensive stroke center.",
      },
    ],
    relatedServicePath: "/tele-stroke",
    relatedServiceLabel: "See Beam Tele-Stroke",
  },
  {
    slug: "tele-nephrology",
    title: "What Is Tele-Nephrology? Definition & Uses | Beam",
    description:
      "Tele-nephrology is remote nephrology consultation for CKD, AKI, electrolytes, and pre-dialysis planning. Here's how it works and who uses it.",
    term: "Tele-Nephrology",
    h1: "What is tele-nephrology?",
    answerBox:
      "Tele-nephrology is the delivery of nephrology consultation remotely — curbsides, eConsults, and tele-visits — for cases involving CKD, AKI, electrolyte disturbances, proteinuria, resistant hypertension, and pre-dialysis planning. It's used by rural hospitals and clinics that can't recruit a full-time nephrologist.",
    sections: [
      {
        heading: "What tele-nephrology covers",
        body: "CKD staging and progression planning. AKI follow-up and med-dosing questions. Electrolyte and acid-base disturbances. Proteinuria and hematuria workup. Resistant hypertension optimization. Pre-dialysis planning and vascular access decisions. Delivered as curbsides for quick questions, eConsults for documented opinions, or tele-visits when needed.",
      },
      {
        heading: "Who uses tele-nephrology",
        body: "Rural and community hospitals for inpatient nephrology consultation. Primary care clinics for CKD comanagement. Anywhere a nephrologist is a six-week wait away — which is most of the country outside major metros.",
      },
      {
        heading: "The premium standard",
        body: "The consulting physician should be a board-certified nephrologist with meaningful post-fellowship experience. Beam requires NPI verification, board certification, and a minimum of 3 years post-residency on every tele-nephrology case.",
      },
    ],
    faqs: [
      {
        q: "Is tele-nephrology billable?",
        a: "Yes. Tele-visits are billed as standard outpatient E/M codes. Provider-to-provider eConsults are billable under CPT 99451 and 99452 when documentation criteria are met.",
      },
      {
        q: "Can tele-nephrology support dialysis patients?",
        a: "It supports the pre-dialysis planning stage — access decisions, education, and timing. Chronic dialysis rounding still typically requires in-person or telemedicine at the dialysis unit itself.",
      },
      {
        q: "Who qualifies as a tele-nephrologist?",
        a: "A board-certified nephrologist with an active US state medical license. Premium platforms require NPI verification and a minimum of 3 years post-fellowship experience.",
      },
    ],
    relatedServicePath: "/tele-nephrology",
    relatedServiceLabel: "See Beam Tele-Nephrology",
  },
  {
    slug: "tele-hematology",
    title: "What Is Tele-Hematology? Definition & Uses | Beam",
    description:
      "Tele-hematology is remote hematology consultation for anemia, low platelets, clotting, and anticoagulation questions. Here's how it works and who uses it.",
    term: "Tele-Hematology",
    h1: "What is tele-hematology?",
    answerBox:
      "Tele-hematology is the delivery of hematology consultation remotely — curbsides, eConsults, and tele-visits — for cases involving anemia, thrombocytopenia, abnormal white counts, clotting and bleeding disorders, and anticoagulation management. Community hospitals use it to stabilize patients locally and avoid transfers for questions a hematologist can answer the same day.",
    sections: [
      {
        heading: "What tele-hematology covers",
        body: "Anemia workup and iron studies. Thrombocytopenia and abnormal CBC review. Leukocytosis and leukopenia triage. DVT and PE management. Anticoagulation starts, holds, and reversal questions. Bleeding and clotting disorder workups. Sickle cell pain crisis support. Transfusion decision support. Delivered as curbsides for quick questions, eConsults for documented opinions, or tele-visits when the patient needs one.",
      },
      {
        heading: "How it reduces unnecessary transfers",
        body: "Many transfers out of community hospitals happen because no one on-site can interpret an abnormal count or confirm a transfusion or anticoagulation plan. A hematologist reachable in minutes can confirm the patient is safe to keep, tell the team what to watch, and document the plan — so the bed, the revenue, and the patient stay local.",
      },
      {
        heading: "The premium standard",
        body: "The consulting physician should be a board-certified hematologist with meaningful post-fellowship experience. Beam requires NPI verification, board certification, and a minimum of 3 years post-residency on every tele-hematology case.",
      },
    ],
    faqs: [
      {
        q: "Is tele-hematology billable?",
        a: "Yes. Tele-visits are billed as standard outpatient E/M codes. Provider-to-provider eConsults are billable under CPT 99451 and 99452 when documentation criteria are met.",
      },
      {
        q: "Can tele-hematology support inpatients?",
        a: "Yes. Curbside and eConsult support for inpatient anemia, thrombocytopenia, anticoagulation, and transfusion questions is one of the most common uses at community and critical-access hospitals.",
      },
      {
        q: "Who qualifies as a tele-hematologist?",
        a: "A board-certified hematologist (often hematology/oncology) with an active US state medical license. Premium platforms require NPI verification and a minimum of 3 years post-fellowship experience.",
      },
    ],
    relatedServicePath: "/tele-hematology",
    relatedServiceLabel: "See Beam Tele-Hematology",
  },
];


export const WHAT_IS_BY_SLUG: Record<string, WhatIsEntry> = Object.fromEntries(
  WHAT_IS_ENTRIES.map((e) => [e.slug, e]),
);
